// src/api.js

import { auth } from "./firebase";
import { signInAnonymously, getIdToken } from "firebase/auth";

/* ────────────────────────────────────────────────────────────────────────────
   API base URL

   Two bugs fixed here:

   1. The old value was the literal string "http://localhost:8000". On a phone
      hitting http://192.168.3.195:3000, "localhost" resolves to the PHONE, not
      to the MacBook running uvicorn — so every request died. Deriving the host
      from window.location.hostname makes one rule work from both devices.

   2. The localhost branch omitted "/api". Every backend route is mounted under
      APIRouter(prefix="/api") (main.py:50), so fetchItems() was requesting
      /items and getting a 404 locally. Production worked only because the
      "/api" branch happened to be correct.

   In production the Firebase Hosting rewrite maps /api/** to Cloud Run, so a
   relative base is right there.
   ──────────────────────────────────────────────────────────────────────────── */

const { hostname, protocol } = window.location;

const isLocalDev =
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.startsWith("192.168.") ||   // typical home/office LAN
  hostname.startsWith("10.") ||        // some routers
  /^172\.(1[6-9]|2\d|3[01])\./.test(hostname); // 172.16-31.x.x

export const API_URL =
  process.env.REACT_APP_API_BASE ||
  (isLocalDev ? `${protocol}//${hostname}:8000/api` : "/api");

// Handy when debugging from a phone, where there's no console to hand.
if (isLocalDev) {
  console.info("🔌 API base:", API_URL);
}

/* ────────────────────────────────────────────────────────────────────────────
   Shared request helper

   Centralises error handling so every caller gets the backend's actual
   `detail` message rather than a bare status code. The new admin.py returns
   503 with an actionable message; without this, that message was discarded.
   ──────────────────────────────────────────────────────────────────────────── */

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_URL}${path}`, options);
  } catch (networkErr) {
    // fetch() rejects only on network failure, never on a 4xx/5xx.
    throw new ApiError(
      "Could not reach the auction service. Check your connection and try again.",
      0
    );
  }

  if (!res.ok) {
    const detail = await res.json().then((d) => d.detail).catch(() => null);
    throw new ApiError(detail || `Request failed (${res.status})`, res.status);
  }

  return res.status === 204 ? null : res.json();
}

/**
 * Firebase anonymous auth header, for endpoints that require it.
 */
async function getAuthHeaders() {
  try {
    const { user } = await signInAnonymously(auth);
    const token = await getIdToken(user);
    return { Authorization: `Bearer ${token}` };
  } catch (e) {
    console.warn("🔒 Anonymous Firebase auth failed:", e);
    return {};
  }
}

/* ── PUBLIC ──────────────────────────────────────────────────────────────── */

export async function fetchItems() {
  const data = await request("/items");
  // The backend returns a dict keyed by item id; callers expect an array.
  return Array.isArray(data) ? data : Object.values(data);
}

export async function getItemDetails(itemId) {
  const items = await fetchItems();
  return items.find((i) => i.id === itemId);
}

export function submitBid(itemId, bidData) {
  return request("/bid", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ item_id: itemId, ...bidData }),
  });
}

export function fetchChapters() {
  return request("/cartoon/chapters");
}

export async function fetchCartoonEpisodes() {
  const chapters = await fetchChapters();
  return chapters.flatMap((ch) => ch.episodes);
}

/* ── ADMIN ───────────────────────────────────────────────────────────────── */

/**
 * NOTE: AdminPage authenticates with the HMAC token in localStorage.adminToken,
 * not with Firebase. This function uses Firebase anonymous auth and is likely
 * stale — worth reconciling with AdminPage's own fetch when you consolidate.
 */
export async function fetchAdminData() {
  const headers = await getAuthHeaders();
  return request("/admin", { headers });
}

export function generateAllQRs() {
  return request("/generate-all-qr");
}

export function downloadHomepageQR() {
  return request("/generate-homepage-qr").then(() =>
    window.open(`${API_URL}/download-homepage-qr`, "_blank")
  );
}

export function downloadCatalogPDF() {
  window.open(`${API_URL}/download-pdf-catalog`, "_blank");
}

export function downloadQR(itemId) {
  window.open(`${API_URL}/download-qr/${itemId}`, "_blank");
}

export function exportCSV() {
  window.open(`${API_URL}/export-bids-csv`, "_blank");
}